import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Copy,
  Lightbulb,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import { useRecommendation } from "@/hooks/use-kb-insights";
import type {
  Recommendation,
  ArticleContent,
  Evidence,
} from "@/hooks/use-kb-insights";
import { useAllTenants } from "@/hooks/use-tenants";
import { tenantLabel } from "@/lib/cells/cells";
import {
  Page,
  PageContent,
  PageHeader,
  PageTitle,
} from "@/components/common/page";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ConversationModal } from "@/components/kb-insights/conversation-modal";

export const Route = createFileRoute("/agent/kb-insights/$recId")({
  component: RecommendationDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tenant_id: typeof search.tenant_id === "string" ? search.tenant_id : "",
  }),
});

function RecommendationDetailPage() {
  const { recId } = Route.useParams();
  const { tenant_id } = Route.useSearch();
  const {
    data: rawRec,
    isLoading,
    error,
  } = useRecommendation(recId, tenant_id || undefined);
  const { data: tenants } = useAllTenants();
  const [convModalIndex, setConvModalIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <Page>
        <PageHeader>
          <BackLink />
        </PageHeader>
        <div className="flex flex-1 items-center justify-center">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
        </div>
      </Page>
    );
  }

  if (error || !rawRec) {
    return (
      <Page>
        <PageHeader>
          <BackLink />
        </PageHeader>
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <p className="text-destructive text-sm">
            Failed to load recommendation
          </p>
          <p className="text-muted-foreground text-xs">{recId}</p>
        </div>
      </Page>
    );
  }

  const rec = rawRec as unknown as Recommendation;
  const content = rec.content as unknown as Record<string, unknown>;
  const evidenceRaw = rec.evidence as unknown as Record<string, unknown>;

  const rawContent = rec.content as Partial<ArticleContent>;
  const article: ArticleContent = {
    title: rawContent.title ?? "",
    representative_intent: rawContent.representative_intent ?? "",
    insight: rawContent.insight ?? "",
    symptoms: rawContent.symptoms ?? [],
    prerequisites: rawContent.prerequisites ?? [],
    resolution_steps: rawContent.resolution_steps ?? [],
    key_insights: rawContent.key_insights ?? [],
    edge_cases: rawContent.edge_cases ?? [],
    self_serviceable: rawContent.self_serviceable ?? false,
    self_serviceable_reason: rawContent.self_serviceable_reason ?? "",
  };

  const conversationIds: string[] =
    (evidenceRaw.conversation_ids as string[] | undefined) ||
    (evidenceRaw.supporting_conversation_ids as string[] | undefined) ||
    [];
  const ticketIds: string[] =
    (evidenceRaw.ticket_ids as string[] | undefined) || [];
  const rawEvidence = rec.evidence as Partial<Evidence>;
  const evidence: Evidence = {
    supporting_conversation_ids: conversationIds,
    conversation_count:
      rawEvidence.conversation_count ?? conversationIds.length,
    resolution_consensus: rawEvidence.resolution_consensus ?? "",
    outlier_resolutions: rawEvidence.outlier_resolutions ?? [],
    common_symptoms: rawEvidence.common_symptoms ?? [],
  };

  const steps =
    (content.steps as
      | { actor: string; action: string; condition: string | null }[]
      | undefined) || [];
  const hasSteps = steps.length > 0;
  const isCatalog = (rec as Record<string, unknown>).kind === "CATALOGUE";
  const catalogContent = isCatalog
    ? (rec.content as unknown as Record<string, unknown>)
    : null;
  const evaluation = (rec as Record<string, unknown>).evaluation as Record<
    string,
    unknown
  > | null;
  const clusterNotes = (evidenceRaw.cluster_notes as string | undefined) || "";
  const sourceType = ticketIds.length > 0 ? "tickets" : "conversations";

  const tenant = tenants?.find((t) => t.id === rec.tenant_id);
  const title =
    (content.title as string) ||
    (catalogContent?.name as string) ||
    rec.representative_intent ||
    "Untitled";
  const sourceCount = rec.conversation_count ?? evidence.conversation_count;

  return (
    <Page>
      <PageHeader className="@sm:flex-col flex-col items-start gap-2">
        <BackLink tenantId={rec.tenant_id} />
        <div className="flex w-full items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <PageTitle className="text-2xl">{title}</PageTitle>
            {rec.representative_intent &&
              rec.representative_intent !== title && (
                <p className="text-muted-foreground mt-1 text-sm">
                  {rec.representative_intent}
                </p>
              )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              const url = `${window.location.origin}/agent/kb-insights/${recId}`;
              void navigator.clipboard.writeText(url).then(() => {
                toast.success("Link copied");
              });
            }}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" />
            Copy link
          </Button>
        </div>
      </PageHeader>

      <PageContent className="@md:px-0 overflow-y-auto px-4 pb-8">
        {/* Meta badges */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <StatusBadge status={rec.status} />
          <Badge
            variant={rec.type === "GAP" ? "default" : "secondary"}
            className="text-xs"
          >
            {rec.type}
          </Badge>
          <ClassificationBadge value={rec.self_serviceable} />
          {rec.category && (
            <Badge variant="outline" className="text-xs">
              {rec.category}
            </Badge>
          )}
          {rec.sub_category && (
            <Badge variant="outline" className="text-xs">
              {rec.sub_category}
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            {isCatalog ? "Catalog" : hasSteps ? "Escalation" : "KB Article"}
          </Badge>
          {tenant && (
            <Badge variant="secondary" className="text-xs">
              {tenantLabel(tenant)}
            </Badge>
          )}
        </div>

        {/* Insight callout */}
        {(rec.insight || clusterNotes) && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
            <Lightbulb className="mt-0.5 size-5 shrink-0 text-blue-600" />
            <p className="text-sm leading-relaxed text-blue-900 dark:text-blue-100">
              {rec.insight || clusterNotes}
            </p>
          </div>
        )}

        {/* Main content grid */}
        <div className="@lg:grid-cols-3 mb-6 grid grid-cols-1 gap-6">
          {/* Left: Content (2 cols) */}
          <div className="@lg:col-span-2 space-y-6">
            <Tabs defaultValue="content">
              <TabsList>
                <TabsTrigger value="content" className="text-xs">
                  {isCatalog ? "Catalog Item" : hasSteps ? "Steps" : "Article"}
                </TabsTrigger>
                <TabsTrigger value="evidence" className="text-xs">
                  Evidence
                </TabsTrigger>
                {evaluation && (
                  <TabsTrigger value="evaluation" className="text-xs">
                    Evaluation
                  </TabsTrigger>
                )}
                <TabsTrigger value="json" className="text-xs">
                  Raw JSON
                </TabsTrigger>
              </TabsList>

              {/* Content tab */}
              <TabsContent value="content" className="mt-4 space-y-4">
                {isCatalog && catalogContent ? (
                  <CatalogView content={catalogContent} />
                ) : hasSteps ? (
                  <StepsView
                    steps={steps}
                    resolutionAction={
                      (content.resolution_action as string | undefined) || ""
                    }
                  />
                ) : (
                  <ArticleView article={article} />
                )}
              </TabsContent>

              {/* Evidence tab */}
              <TabsContent value="evidence" className="mt-4 space-y-4">
                {evidence.resolution_consensus && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Resolution Consensus
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-relaxed">
                        {evidence.resolution_consensus}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {evidence.common_symptoms.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Common Symptoms</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-1.5">
                        {evidence.common_symptoms.map((s) => (
                          <Badge
                            key={s}
                            variant="outline"
                            className="text-xs font-normal"
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {evidence.outlier_resolutions.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Outlier Resolutions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1.5 text-sm">
                        {evidence.outlier_resolutions.map((r) => (
                          <li key={r} className="flex items-start gap-2">
                            <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-current text-amber-500" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Evaluation tab */}
              {evaluation && (
                <TabsContent value="evaluation" className="mt-4 space-y-4">
                  <EvaluationView evaluation={evaluation} />
                </TabsContent>
              )}

              {/* JSON tab */}
              <TabsContent value="json" className="relative mt-4">
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute right-2 top-2 size-7"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(JSON.stringify(rawRec, null, 2))
                      .then(() => {
                        toast.success("Copied");
                      });
                  }}
                >
                  <Copy className="size-3.5" />
                </Button>
                <pre className="bg-muted/30 max-h-[600px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border p-4 font-mono text-xs">
                  {JSON.stringify(rawRec, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right sidebar: Stats + Sources */}
          <div className="space-y-4">
            {/* Stats card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <MetaRow label="Sources" value={String(sourceCount)} />
                {(rec.confidence_score as number | null) != null && (
                  <MetaRow
                    label="Confidence"
                    value={`${(rec.confidence_score * 100).toFixed(0)}%`}
                  />
                )}
                <MetaRow label="Status" value={rec.status} />
                <MetaRow
                  label="Kind"
                  value={
                    isCatalog
                      ? "Catalog"
                      : (rec as Record<string, unknown>).kind ===
                          "structured_escalation"
                        ? "Escalation"
                        : "KB Article"
                  }
                />
                {evaluation?.verdict ? (
                  <MetaRow
                    label="Verdict"
                    value={(evaluation.verdict as string).replace(/_/g, " ")}
                  />
                ) : null}
                <MetaRow
                  label="Created"
                  value={new Date(rec.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                />
                {rec.updated_at && (
                  <MetaRow
                    label="Updated"
                    value={new Date(rec.updated_at).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      },
                    )}
                  />
                )}
                {article.self_serviceable_reason && (
                  <div className="border-t pt-2">
                    <p className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">
                      {article.self_serviceable
                        ? "Why self-serviceable"
                        : "Why IT needed"}
                    </p>
                    <p className="text-xs leading-relaxed">
                      {article.self_serviceable_reason}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sources list */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="text-muted-foreground h-4 w-4" />
                  <CardTitle className="text-sm">
                    {sourceType === "tickets" ? "Tickets" : "Sources"} (
                    {sourceType === "tickets"
                      ? ticketIds.length
                      : evidence.supporting_conversation_ids.length}
                    )
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="max-h-[400px] space-y-1.5 overflow-y-auto">
                {sourceType === "tickets" ? (
                  ticketIds.length === 0 ? (
                    <p className="text-muted-foreground text-xs">
                      No tickets linked
                    </p>
                  ) : (
                    ticketIds.map((ticketId, idx) => (
                      <TicketRow
                        key={ticketId}
                        ticketId={ticketId}
                        tenantId={rec.tenant_id}
                        // eslint-disable-next-line security/detect-object-injection
                        conversationId={conversationIds[idx]}
                        onViewConversation={() => {
                          setConvModalIndex(idx);
                        }}
                      />
                    ))
                  )
                ) : evidence.supporting_conversation_ids.length === 0 ? (
                  <p className="text-muted-foreground text-xs">
                    No source conversations linked
                  </p>
                ) : (
                  evidence.supporting_conversation_ids.map((convId, idx) => (
                    <SourceRow
                      key={convId}
                      convId={convId}
                      index={idx}
                      tenantId={rec.tenant_id}
                      onClick={() => {
                        setConvModalIndex(idx);
                      }}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </PageContent>

      {/* Conversation modal */}
      {convModalIndex !== null && (
        <ConversationModal
          recommendation={rec}
          conversationIndex={convModalIndex}
          open
          onClose={() => {
            setConvModalIndex(null);
          }}
          onNavigate={setConvModalIndex}
        />
      )}
    </Page>
  );
}

function BackLink({ tenantId }: { tenantId?: string }) {
  return (
    <Link
      to="/agent/kb-insights"
      search={tenantId ? { tenant_id: tenantId } : {}}
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition-colors"
    >
      <ArrowLeft className="size-4" />
      Back to insights
    </Link>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors = new Map([
    ["DRAFT", "bg-yellow-100 text-yellow-700 border-yellow-200"],
    ["APPROVED", "bg-green-100 text-green-700 border-green-200"],
    ["DISMISSED", "bg-gray-100 text-gray-500 border-gray-200"],
    ["DRIFTED", "bg-orange-100 text-orange-700 border-orange-200"],
  ]);
  return (
    <Badge variant="outline" className={`text-xs ${colors.get(status) ?? ""}`}>
      {status}
    </Badge>
  );
}

function ClassificationBadge({ value }: { value: string | boolean | null }) {
  if (value === true || value === "deflectable") {
    return (
      <Badge
        variant="outline"
        className="border-green-200 text-xs text-green-600"
      >
        Deflectable
      </Badge>
    );
  }
  if (value === "potentially_deflectable") {
    return (
      <Badge
        variant="outline"
        className="border-emerald-200 text-xs text-emerald-500"
      >
        Potentially Deflectable
      </Badge>
    );
  }
  if (value === "partially_deflectable") {
    return (
      <Badge
        variant="outline"
        className="border-yellow-200 text-xs text-yellow-600"
      >
        Partially Deflectable
      </Badge>
    );
  }
  if (value === "not_deflectable") {
    return (
      <Badge variant="outline" className="border-red-200 text-xs text-red-500">
        Not Deflectable
      </Badge>
    );
  }
  if (value === "automatable") {
    return (
      <Badge
        variant="outline"
        className="border-blue-200 text-xs text-blue-600"
      >
        Automatable
      </Badge>
    );
  }
  if (value === "automatable_with_approval") {
    return (
      <Badge
        variant="outline"
        className="border-violet-200 text-xs text-violet-600"
      >
        Auto+Approval
      </Badge>
    );
  }
  if (value === "structured_escalation") {
    return (
      <Badge
        variant="outline"
        className="border-amber-200 text-xs text-amber-600"
      >
        Escalation
      </Badge>
    );
  }
  if (value === "junk") {
    return (
      <Badge
        variant="outline"
        className="border-gray-200 text-xs text-gray-400"
      >
        Junk
      </Badge>
    );
  }
  return null;
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}

function ArticleView({ article }: { article: ArticleContent }) {
  return (
    <div className="space-y-5">
      {article.representative_intent && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Intent</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">
              {article.representative_intent}
            </p>
          </CardContent>
        </Card>
      )}

      {article.symptoms.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Symptoms</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {article.symptoms.map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <span className="text-muted-foreground mt-1.5 size-1.5 shrink-0 rounded-full bg-current" />
                  {s}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {article.resolution_steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Resolution Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2 text-sm">
              {article.resolution_steps.map((s, i) => (
                <li key={s} className="flex items-start gap-3">
                  <span className="bg-primary/10 text-primary mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                    {i + 1}
                  </span>
                  <span className="pt-0.5 leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {article.key_insights.length > 0 && (
        <CollapsibleCard title="Key Insights" items={article.key_insights} />
      )}

      {article.edge_cases.length > 0 && (
        <CollapsibleCard title="Edge Cases" items={article.edge_cases} />
      )}
    </div>
  );
}

function CatalogView({ content }: { content: Record<string, unknown> }) {
  const description = (content.description ?? "") as string;
  const ticketType = (content.ticket_type ?? "") as string;
  const priority = (content.priority ?? "") as string;
  const intent = (content.representative_intent ?? "") as string;
  const formFields = (content.form_fields ?? []) as {
    label: string;
    type: string;
    required: boolean;
    options: string[];
    placeholder: string;
  }[];

  return (
    <div className="space-y-5">
      {description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{description}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {ticketType && (
          <Badge variant="outline" className="text-xs">
            Type: {ticketType}
          </Badge>
        )}
        {priority && (
          <Badge variant="outline" className="text-xs">
            Priority: {priority}
          </Badge>
        )}
      </div>

      {intent && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Intent</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{intent}</p>
          </CardContent>
        </Card>
      )}

      {formFields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Form Fields</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {formFields.map((f) => (
              <div key={f.label} className="rounded-md border p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-sm font-medium">{f.label}</span>
                  <Badge variant="outline" className="text-[9px]">
                    {f.type}
                  </Badge>
                  {f.required && (
                    <Badge variant="destructive" className="text-[9px]">
                      Required
                    </Badge>
                  )}
                </div>
                {f.placeholder && (
                  <p className="text-muted-foreground text-xs">
                    {f.placeholder}
                  </p>
                )}
                {f.options.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {f.options.map((o) => (
                      <Badge key={o} variant="secondary" className="text-[9px]">
                        {o}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CollapsibleCard({ title, items }: { title: string; items: string[] }) {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <CardHeader>
        <button
          type="button"
          className="flex w-full items-center justify-between text-left"
          onClick={() => {
            setOpen(!open);
          }}
        >
          <CardTitle className="text-sm">
            {title} ({items.length})
          </CardTitle>
          {open ? (
            <ChevronUp className="text-muted-foreground size-4" />
          ) : (
            <ChevronDown className="text-muted-foreground size-4" />
          )}
        </button>
      </CardHeader>
      {open && (
        <CardContent>
          <ul className="space-y-1.5 text-sm">
            {items.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="text-muted-foreground mt-1.5 size-1.5 shrink-0 rounded-full bg-current" />
                {item}
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}

function StepsView({
  steps,
  resolutionAction,
}: {
  steps: { actor: string; action: string; condition: string | null }[];
  resolutionAction: string;
}) {
  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Resolution Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-sm">
            {steps.map((step, i) => (
              <li key={step.action} className="relative">
                {step.condition ? (
                  <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700 dark:bg-amber-950/30">
                    <div className="mb-2 flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="border-amber-400 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
                      >
                        If
                      </Badge>
                      <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                        {step.condition}
                      </span>
                    </div>
                    <div className="ml-1 flex items-start gap-3 border-l-2 border-amber-300 pl-3 dark:border-amber-700">
                      <span className="bg-primary/10 text-primary mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="mb-0.5 flex items-center gap-2">
                          <Badge
                            variant={
                              step.actor === "agent" ? "default" : "secondary"
                            }
                            className="text-[9px]"
                          >
                            {step.actor}
                          </Badge>
                        </div>
                        <p className="leading-relaxed text-amber-950 dark:text-amber-50">
                          {step.action}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <span className="bg-primary/10 text-primary mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="mb-0.5 flex items-center gap-2">
                        <Badge
                          variant={
                            step.actor === "agent" ? "default" : "secondary"
                          }
                          className="text-[9px]"
                        >
                          {step.actor}
                        </Badge>
                      </div>
                      <p className="leading-relaxed">{step.action}</p>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {resolutionAction && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Resolution Action</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{resolutionAction}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function EvaluationView({
  evaluation,
}: {
  evaluation: Record<string, unknown>;
}) {
  const verdict = (evaluation.verdict as string | undefined) || "";
  const verdictNote = (evaluation.verdict_note as string | undefined) || "";
  const classificationNote =
    (evaluation.classification_note as string | undefined) || "";
  const stepOrderingAnalysis =
    (evaluation.step_ordering_analysis as string | undefined) || "";
  const generationQualityNote =
    (evaluation.generation_quality_note as string | undefined) || "";
  const stepEvaluations =
    (evaluation.step_evaluations as
      | {
          step_index: number;
          readiness: string;
          note: string;
          sources_sufficient: boolean;
        }[]
      | undefined) || [];

  return (
    <div className="space-y-4">
      {verdict && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm">Verdict</CardTitle>
              <Badge variant="outline" className="text-xs capitalize">
                {verdict.replace(/_/g, " ")}
              </Badge>
            </div>
          </CardHeader>
          {verdictNote && (
            <CardContent>
              <p className="text-sm leading-relaxed">{verdictNote}</p>
            </CardContent>
          )}
        </Card>
      )}

      {classificationNote && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Classification Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{classificationNote}</p>
          </CardContent>
        </Card>
      )}

      {stepEvaluations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Step Evaluations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stepEvaluations.map((se) => (
              <div key={se.step_index} className="rounded-md border p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-medium">
                    Step {se.step_index}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[9px] ${se.readiness === "ready" ? "border-green-200 text-green-600" : "border-yellow-200 text-yellow-600"}`}
                  >
                    {se.readiness}
                  </Badge>
                  {se.sources_sufficient && (
                    <Badge
                      variant="outline"
                      className="border-blue-200 text-[9px] text-blue-600"
                    >
                      Sources OK
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {se.note}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {stepOrderingAnalysis && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Step Ordering Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{stepOrderingAnalysis}</p>
          </CardContent>
        </Card>
      )}

      {generationQualityNote && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Generation Quality</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">{generationQualityNote}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-4">
        {evaluation.resolution_depth != null && (
          <MetaRow
            label="Resolution Depth"
            value={`${Number(evaluation.resolution_depth)}/5`}
          />
        )}
        {evaluation.step_specificity != null && (
          <MetaRow
            label="Step Specificity"
            value={`${Number(evaluation.step_specificity)}/5`}
          />
        )}
        {evaluation.evidence_grounding != null && (
          <MetaRow
            label="Evidence Grounding"
            value={`${Number(evaluation.evidence_grounding)}/5`}
          />
        )}
        {evaluation.generation_quality != null && (
          <MetaRow
            label="Generation Quality"
            value={`${Number(evaluation.generation_quality)}/5`}
          />
        )}
        {evaluation.automation_feasibility != null && (
          <MetaRow
            label="Automation Feasibility"
            value={`${Number(evaluation.automation_feasibility)}/5`}
          />
        )}
        {evaluation.classification_accuracy != null && (
          <MetaRow
            label="Classification Accuracy"
            value={`${Number(evaluation.classification_accuracy)}/5`}
          />
        )}
      </div>
    </div>
  );
}

function TicketRow({
  ticketId,
  conversationId,
  onViewConversation,
}: {
  ticketId: string;
  tenantId: string;
  conversationId?: string;
  onViewConversation: () => void;
}) {
  return (
    <div className="hover:bg-accent/50 flex items-center gap-1.5 rounded-lg border px-3 py-2 transition-colors">
      <button
        type="button"
        className="min-w-0 flex-1 cursor-pointer text-left"
        onClick={onViewConversation}
      >
        <span className="text-xs font-medium">{ticketId}</span>
        {conversationId && (
          <p className="text-muted-foreground/60 mt-0.5 truncate font-mono text-[10px]">
            {conversationId}
          </p>
        )}
      </button>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-6"
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard.writeText(ticketId).then(() => {
                  toast.success("Copied ticket ID");
                });
              }}
            >
              <Copy className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy ticket ID</TooltipContent>
        </Tooltip>
        {conversationId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="size-6"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewConversation();
                }}
              >
                <ExternalLink className="text-muted-foreground size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View conversation</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function SourceRow({
  convId,
  index,
  onClick,
}: {
  convId: string;
  index: number;
  tenantId: string;
  onClick: () => void;
}) {
  return (
    <div className="hover:bg-accent/50 flex items-center gap-1.5 rounded-lg border transition-colors">
      <button
        type="button"
        className="flex-1 cursor-pointer p-2.5 text-left"
        onClick={onClick}
      >
        <span className="text-muted-foreground text-[10px]">
          Source #{index + 1}
        </span>
        <p className="text-muted-foreground/60 mt-0.5 truncate font-mono text-xs">
          {convId}
        </p>
      </button>
      <div className="flex items-center gap-1 pr-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-6"
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard.writeText(convId).then(() => {
                  toast.success("Copied ID");
                });
              }}
            >
              <Copy className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy ID</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-6"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <MessageSquare className="text-muted-foreground size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View conversation</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
